import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { NumberInput } from '../ui/NumberInput';
import { RowMenu } from '../ui/RowMenu';
import { PurchasesSegments } from './PurchasesPage';
import { dayLabel, errorText, INVESTMENTS_RESOURCE, PURCHASES_KEY, type Investment } from './types';
import '../finance/finance.css';
import './purchases.css';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';

type Draft = { name: string; category: string; purchaseDate: string; net: string; notes: string };
const empty: Draft = { name: '', category: '', purchaseDate: '', net: '', notes: '' };
const draftOf = (i: Investment): Draft => ({ name: i.name, category: i.category, purchaseDate: i.purchaseDate, net: i.netGrosze === undefined ? '' : plnToInput(i.netGrosze), notes: i.notes });

function InvestmentForm({ initial, label, submitText, pending, error, onSave, onCancel, withAmount }: { initial: Draft; label: string; submitText: string; pending: boolean; error: unknown; onSave: (body: Record<string, unknown>) => Promise<unknown>; onCancel?: () => void; withAmount: boolean }) {
  const [d, setD] = useState(initial);
  const [local, setLocal] = useState('');
  function bodyOf(): Record<string, unknown> | null {
    const body: Record<string, unknown> = { name: d.name, category: d.category, purchaseDate: d.purchaseDate, notes: d.notes };
    if (withAmount) {
      try {
        body.netGrosze = parsePln(d.net);
      } catch (err) {
        return (setLocal((err as Error).message), null);
      }
    }
    setLocal('');
    return body;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = bodyOf();
    // the error is shown by the form (from the mutation)
    if (body) onSave(body).catch(() => undefined);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(d, initial),
    save: async (el) => {
      const body = bodyOf();
      if (!body || !validForm(el)) return false;
      await onSave(body);
      return true;
    },
  });
  const message = local || (error ? errorText(error, 'Nie udało się zapisać') : '');
  return (
    <form ref={guardRef} className="fin-add" aria-label={label} onSubmit={submit}>
      <label className="field">
        <span>Nazwa</span>
        <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="np. bus Iveco" autoComplete="off" />
      </label>
      <label className="field">
        <span>Kategoria</span>
        <input value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })} placeholder="np. Transport" autoComplete="off" />
      </label>
      <label className="field">
        <span>Data zakupu</span>
        <input type="date" value={d.purchaseDate} onChange={(e) => setD({ ...d, purchaseDate: e.target.value })} />
      </label>
      {withAmount && (
        <label className="field">
          <span>Netto (zł)</span>
          <NumberInput kind="money" value={d.net} onValueChange={(net) => setD({ ...d, net })} />
        </label>
      )}
      <button className="btn primary" type="submit" disabled={pending}>
        {submitText}
      </button>
      {onCancel && (
        <button className="btn ghost" type="button" onClick={onCancel}>
          Anuluj
        </button>
      )}
      {message && (
        <p className="alert" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}

/** "Finanse → Zakupy → Inwestycje" (F42): the company's investments, from purchase invoices or entered by hand. */
export function InvestmentsPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const list = useQuery({ queryKey: [...PURCHASES_KEY, 'investments'], queryFn: () => api<{ investments: Investment[] }>('/api/purchases/investments') });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: PURCHASES_KEY });
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/purchases/investments', { method: 'POST', body }),
    onSuccess: () => {
      setAdding(false);
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: ({ i, body }: { i: Investment; body: Record<string, unknown> }) => api(`/api/purchases/investments/${i.id}`, { method: 'PATCH', body: { ...body, version: i.version } }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: (i: Investment) => api(`/api/purchases/investments/${i.id}?version=${i.version}`, { method: 'DELETE' }), onSettled: refresh });
  if (!me) return null;
  const rows = list.data?.investments ?? [];
  const showAmounts = rows.length === 0 ? true : rows.some((r) => r.netGrosze !== undefined);
  const total = rows.reduce((a, r) => a + (r.netGrosze ?? 0), 0);
  const conflict = update.error instanceof ApiError && update.error.status === 409;
  return (
    <section className="page wide purchases-page" data-feature-id="purchases.investments">
      <PurchasesSegments me={me} />
      <div className="purchases-head">
        <p className="lede">Większe zakupy firmy poza sprzętem do wynajmu: z faktur zakupowych i dopisane ręcznie.</p>
        {can(me, INVESTMENTS_RESOURCE, 'create') && !adding && (
          <button className="btn primary" type="button" onClick={() => setAdding(true)}>
            Dodaj inwestycję
          </button>
        )}
      </div>
      {adding && <InvestmentForm initial={empty} label="Nowa inwestycja" submitText="Dodaj inwestycję" pending={create.isPending} error={create.error} onSave={(b) => create.mutateAsync(b)} onCancel={() => setAdding(false)} withAmount={showAmounts} />}
      {list.error && (
        <p className="alert" role="alert">
          {errorText(list.error, 'Nie udało się wczytać inwestycji')}
        </p>
      )}
      {list.data && rows.length === 0 && <p className="muted">Nie ma jeszcze inwestycji.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table" aria-label="Inwestycje">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Nazwa</th>
                <th scope="col" className="wide-only">
                  Kategoria
                </th>
                {showAmounts && (
                  <th scope="col" className="num">
                    Netto
                  </th>
                )}
                <th scope="col" className="wide-only">
                  Faktura
                </th>
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) =>
                editing === i.id ? (
                  <tr key={i.id}>
                    <td colSpan={6}>
                      {conflict && (
                        <ConflictNotice
                          onReload={() => {
                            update.reset();
                            setEditing(null);
                            refresh();
                          }}
                        />
                      )}
                      <InvestmentForm initial={draftOf(i)} label={`Zmiana: ${i.name}`} submitText="Zapisz" pending={update.isPending} error={conflict ? null : update.error} onSave={(body) => update.mutateAsync({ i, body })} onCancel={() => setEditing(null)} withAmount={i.netGrosze !== undefined} />
                    </td>
                  </tr>
                ) : (
                  <tr key={i.id}>
                    <td className="nowrap">{dayLabel(i.purchaseDate)}</td>
                    <td>{i.name}</td>
                    <td className="wide-only">{i.category}</td>
                    {showAmounts && <td className="num nowrap">{i.netGrosze === undefined ? '—' : formatPln(i.netGrosze)}</td>}
                    <td className="wide-only">{i.purchase ? <Link to={`/finanse/zakupy/${i.purchase.id}`}>{i.purchase.label}</Link> : <span className="muted">dopisana ręcznie</span>}</td>
                    <td className="q-actions">
                      <RowMenu
                        label={`Działania: ${i.name}`}
                        actions={[
                          ...(can(me, INVESTMENTS_RESOURCE, 'edit') ? [{ label: 'Zmień', onSelect: () => leaveGuarded(() => setEditing(i.id)) }] : []),
                          ...(can(me, INVESTMENTS_RESOURCE, 'delete') ? [{ label: 'Usuń', onSelect: () => remove.mutate(i) }] : []),
                        ]}
                      />
                    </td>
                  </tr>
                ),
              )}
            </tbody>
            {showAmounts && (
              <tfoot>
                <tr>
                  <th scope="row" colSpan={2}>
                    Razem
                  </th>
                  <td className="wide-only" />
                  <td className="num nowrap">{formatPln(total)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {remove.error && (
        <p className="alert" role="alert">
          {errorText(remove.error, 'Nie udało się usunąć')}
        </p>
      )}
    </section>
  );
}
